import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { library } from '@cwos/api-client';
import { Pill, SkeletonSet } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { LoadFailure } from '../LoadFailure.js';
import { RuleDependents } from './RuleDependents.js';
import {
  applicabilitySentence,
  appliesEverywhere,
  bindingnessLabel,
  bindingnessSentence,
  bindingnessVariant,
  effectiveWindow,
  extractionStateLabel,
  formatInstantDay,
  narrowings,
  ruleLevelLabel,
  ruleLevelSentence,
  ruleStateLabel,
  ruleStateVariant,
} from './libraryLogic.js';

/**
 * ONE RULE, EXPLAINED THE WAY SOMEBODY WOULD HAVE TO EXPLAIN IT IN A REVIEW.
 *
 * The founder's requirement for this screen, in order: the rule in plain
 * language, who it applies to, which document it came from, when it took
 * effect, who approved it, what depends on it, and its earlier versions.
 *
 * SIX OF THOSE SEVEN WERE HERE, and the seventh's absence was stated on the
 * screen rather than left as a gap: nothing in this product recorded that a
 * rule was USED, so "what depends on this" was not a question with a thin
 * answer, it was a question with no data behind it at all.
 *
 * THAT CHANGED ON 2026-09-07, FOR ONE CONSUMER. Founder ruling 4's migration
 * (1852000000000) gave `recommendation_doctrine_snapshot` a foreign key to the
 * canonical, versioned rule row that was in force when a recommendation was
 * composed — the rule → decision edge an earlier audit called unbuildable. So
 * the seventh requirement is now partly answerable, and `RuleDependents` below
 * answers it: the tracked list ABOVE the untracked sentence, never instead of
 * it. A "Used by: 3" panel where the truth is "3 recommendations, and four
 * other consumer classes record nothing" would be the same four-state collapse
 * the original absence was written to avoid, wearing a number.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS WITHHELD HERE, AND WHAT IS NOT
 * ---------------------------------------------------------------------------
 * A reader whose programs do not include the rule's SOURCE gets the whole rule
 * — sentence, bindingness, level, dates, approver, history — and does not get
 * the document's name. That split is the server's, and it is deliberate: in
 * this product a document's title names a program, a payer, a service line or a
 * participant, while the rule itself is the organization's own policy and
 * belongs to its people.
 *
 * The screen says so in words. `sourceWithheld` is read BEFORE the null, never
 * after, because a null alone cannot distinguish "you may not see it" from
 * "there is none" — and for a rule the second is impossible, since the source
 * foreign key is NOT NULL.
 */
export function RuleDetail() {
  const navigate = useNav();
  const params = useParams({ strict: false }) as { ruleId?: string };
  const ruleId = params.ruleId ?? '';

  const detail = useQuery({
    queryKey: ['library', 'rule', ruleId],
    queryFn: () => library.rule(ruleId),
    enabled: ruleId !== '',
  });

  if (detail.isLoading) return <SkeletonSet surface="drawer" rows={5} />;
  if (detail.isError) {
    return (
      <LoadFailure
        error={detail.error}
        message="We couldn’t open this rule just now. This is not a statement that it does not exist."
        onRetry={() => void detail.refetch()}
        mainRead
        refusedHeading="This rule is not shown to you"
      />
    );
  }

  const data = detail.data;
  if (!data) return null;
  const { rule, history, extraction } = data;
  const narrowed = narrowings(rule);

  return (
    <article className="rn-col">
      <header className="rn-card">
        <p className="rn-muted">Answers: {rule.subject}</p>
        <h1 className="rn-card-title">{rule.statement}</h1>
        <p className="rn-row">
          <Pill variant={bindingnessVariant(rule.bindingness)}>
            {bindingnessLabel(rule.bindingness)}
          </Pill>{' '}
          <Pill variant={ruleStateVariant(rule.state)}>{ruleStateLabel(rule.state)}</Pill>
        </p>
        <p className="rn-muted">{bindingnessSentence(rule.bindingness)}</p>
      </header>

      <section className="rn-card">
        <h2 className="rn-card-title">Who this applies to</h2>
        <p className="rn-strong">{ruleLevelLabel(rule.ruleLevel)}</p>
        <p className="rn-muted">{ruleLevelSentence(rule.ruleLevel)}</p>
        <p className="rn-muted">{applicabilitySentence(rule)}</p>
        {appliesEverywhere(rule) ? null : (
          <ul>
            {narrowed.map((n) => (
              // THE DIMENSION AND THE COUNT, NEVER THE IDENTIFIERS. The arrays
              // behind these numbers hold UUIDs, and `clientIds` holds
              // participant identifiers — a rule listing has no business
              // disclosing which people a policy names.
              <li key={n.dimension}>
                {n.dimension}: {n.count}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rn-card">
        <h2 className="rn-card-title">When it applies</h2>
        <p className="rn-strong">{effectiveWindow(rule)}</p>
        {rule.state === 'superseded' ? (
          <p className="rn-muted">
            A newer version took over from this one. It is kept, and asking Phibian a question dated
            inside the window above still gets this answer — which is what makes a decision made
            back then explainable now.
          </p>
        ) : null}
      </section>

      <section className="rn-card">
        <h2 className="rn-card-title">Who approved it</h2>
        {/* The approval chain, which the API carries because the row has
            always held it: `approved_by` and `approved_at` are NOT NULL
            columns. The person's NAME is not resolved here — reading the user
            directory is a separate permission, and a screen that quietly
            joined it would widen this one. */}
        <p className="rn-strong">Approved on {formatInstantDay(rule.approvedAt)}</p>
        <p className="rn-muted">
          Version {rule.version}
          {rule.supersedesId ? ', replacing an earlier version' : ', the first version'}. Nothing
          about this rule can be edited: a change is a new version that supersedes it, and the
          approval behind it cannot be withdrawn while the rule stands.
        </p>
        {extraction?.decisionNote ? (
          <p className="rn-muted">The approver noted: “{extraction.decisionNote}”</p>
        ) : null}
      </section>

      <section className="rn-card">
        <h2 className="rn-card-title">Where it came from</h2>
        {/* FLAG FIRST, then the value. See the header. */}
        {rule.sourceWithheld ? (
          <p className="rn-muted">
            Not shown to you. This rule came from a document outside your programs, so Phibian is
            withholding the document’s name — not the rule. Someone with wider knowledge access can
            tell you which one it was.
          </p>
        ) : rule.sourceId ? (
          <p>
            <button type="button" onClick={() => navigate(`/library/sources/${rule.sourceId!}`)}>
              {rule.sourceTitle ?? 'Open the document'}
            </button>
          </p>
        ) : (
          <p className="rn-muted">Unnamed document.</p>
        )}
      </section>

      <section className="rn-card">
        <h2 className="rn-card-title">Earlier versions</h2>
        {history.length <= 1 ? (
          <p className="rn-muted">
            This rule has never been revised. That is a complete history, not a missing one.
          </p>
        ) : (
          <ol>
            {history.map((h) => (
              <li key={h.id}>
                <span className={h.id === rule.id ? 'rn-strong' : undefined}>
                  Version {h.version} — {ruleStateLabel(h.state)} — {effectiveWindow(h)}
                </span>
                {h.id === rule.id ? null : (
                  <>
                    {' '}
                    <button type="button" onClick={() => navigate(`/library/rules/${h.id}`)}>
                      Open
                    </button>
                  </>
                )}
                <p className="rn-muted">{h.statement}</p>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* THE SEVENTH REQUIREMENT, NOW PARTLY ANSWERABLE. See RuleDependents:
          the absence this card used to render whole is kept for every consumer
          class that still records nothing, and the tracked answer sits above
          it rather than instead of it. */}
      <RuleDependents ruleId={rule.id} />

      {extraction ? (
        <section className="rn-card">
          <h2 className="rn-card-title">The reading this came from</h2>
          <p className="rn-muted">
            {extractionStateLabel(extraction.state)} · proposed{' '}
            {formatInstantDay(extraction.proposedAt)}
          </p>
          <p>{extraction.proposedStatement}</p>
          {extraction.proposedStatement === rule.statement ? (
            <p className="rn-muted">
              The approved rule uses the proposed wording exactly. An approver may change how
              binding a rule is and who it applies to; they cannot rewrite the sentence, so a rule
              never has two authors and one signature.
            </p>
          ) : null}
        </section>
      ) : null}
    </article>
  );
}
